<template>
  <div>
    <!-- <load-profile /> -->
    <b-card>
      <b-card-text class="mb-2 d-flex justify-content-between">
        <div />
        <b-button variant="outline-primary" @click="handleCreateGroup">{{
          $t("button.addLanguage")
        }}</b-button>
      </b-card-text>
      <DraggableTree
        ref="DraggableTree"
        :data="items"
        :draggable="true"
        :cross-tree="false"
        :ondragstart="ondragstart"
        :ondragend="ondragend"
      >
        <template v-slot="{ data, store }">
          <template v-if="!data.isDragPlaceHolder">
            <div v-if="data.children && data.children.length" class="text-left">
              <command
                :item="data"
                :store="store"
                :locales="locales"
                @onCreate="handleCreate"
                @onUpdate="handleUpdate"
                @onDelete="handleDelete"
              />
            </div>
            <div v-else>
              <command
                :item="data"
                :store="store"
                :locales="locales"
                @onCreate="handleCreate"
                @onUpdate="handleUpdate"
                @onDelete="handleDelete"
              />
            </div>
          </template>
        </template>
      </DraggableTree>
    </b-card>

    <b-modal
      id="modal-form"
      ref="modal-form"
      hide-footer
      content-class="shadow"
      :title="$t('title.voiceCommand')"
    >
      <validation-observer ref="editForm" #default="{ invalid }">
        <b-form
          key="editForm"
          v-shortkey="['ctrl', 'enter']"
          @submit.prevent="validateForm"
          @shortkey="validateForm()"
        >
          <n-input v-model="data" :fields="fields" />
          <b-row>
            <b-col cols="12" class="text-right my-1">
              <b-button
                variant="outline-default"
                type="button"
                class="mr-1"
                @click="hideModal"
              >
                {{ $t("button.cancel") }}
              </b-button>
              <n-button-loading
                v-if="$can('update', permissionType)"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="outline-primary"
                :loading="loading"
                :disabled="invalid"
              >
                {{ $t("button.save") }}
              </n-button-loading>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardText,
  BLink,
  BModal,
  BButton,
  BButtonGroup,
  BForm,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import NButtonLoading from "@/components/NButtonLoading";
import NFormConfirmation from "@/components/NFormConfirmation";
import NInput from "@/components/NInput";
import Repository from "@/repositories/RepositoryFactory";
import { DraggableTree } from "vue-draggable-nested-tree";

import languageEn from "@/data/languages/en";
import languageKm from "@/data/languages/km";
import languageVi from "@/data/languages/vi";
import languageZh from "@/data/languages/zh-cn";
import { scrollOnMouseMove, unsetScrollOnMouseMove } from "./helper";
import FormInputGroup from "./formInputGroup";
import Command from "./Command.vue";
import FormInput from "./formInput";

const CallCenterRegistry = Repository.get("callCenter");
const FileRepository = Repository.get("file");

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BLink,
    BModal,
    BButtonGroup,
    BButton,
    BForm,
    NButtonLoading,
    NFormConfirmation,
    NInput,
    DraggableTree,
    Command,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      data: {
        parent: null,
        onKey: null,
        localeCode: null,
        voice: null,
        backVoice: null,
        waitingSound: null,
        ringTune: null,
        command: "tree",
        memo: null,
      },
      originItems: [],
      items: [],
      drag: false,
      initData: {},
      loading: false,
      oldParent: null,
    };
  },
  computed: {
    locales() {
      if (this.$i18n.locale === "km") {
        return this.languageKm;
      } else if (this.$i18n.locale === "en") {
        return this.languageEn;
      } else if (this.$i18n.locale === "vi") {
        return this.languageVi;
      }

      return this.languageZh;
    },

    fieldsGroupComputed() {
      return this.fieldsGroup.map((e) => {
        if (e.key === "localeCode") {
          e.options = this.locales;
        }
        return e;
      });
    },
  },
  mounted() {
    this.show();
  },
  methods: {
    showModal() {
      this.$refs["modal-form"].show();
    },
    hideModal() {
      this.$refs["modal-form"].hide();
    },
    ondragstart(data, store) {
      const { parent } = data;
      if (parent.isRoot) {
        this.items = this.items.map((e) => {
          e.droppable = false;
          return e;
        });
      } else {
        const rootNode = this.items[0].parent;
        rootNode._vm.$el?.classList.add("dragging-child");
      }
      this.oldParent = { ...parent };

      scrollOnMouseMove();
    },
    ondragend(data, store) {
      this.$nextTick(() => {
        const { parent } = data;
        const sameKey = parent.children.find(
          (e) => e.onKey == data.onKey && e.id != data.id
        );

        if ((this.oldParent.isRoot && !parent.isRoot) || sameKey) {
          this.updateTree();
          this.$swal.fire({
            text: this.$t("alert.cannotMove__duplicatedCommandKey"),
            icon: "warning",
            confirmButtonText: this.$t("button.close"),
          });
          return;
        }

        // Update parent
        if (this.oldParent && parent._id !== this.oldParent?._id) {
          data.parentId = parent.id;
          data.loading = true;
          const { parentId, id } = data;
          CallCenterRegistry.instruction
            .update({ parent: parentId || null }, id)
            .then((response) => {
              const data = { ...response.data.data };
              this.originItems.map((e) => {
                if (e.id == data.id) {
                  for (const key of data) {
                    e[key] = data[key];
                  }
                }
                return e;
              });
            })
            .catch()
            .then(() => {
              this.hideModal();
              data.loading = false;
            });
        }

        const items = parent.children;
        const index = items.findIndex((e) => e.id === data.id);

        if (index !== data.sorting) {
          const params = items.map((e, i) => ({ id: e.id, sorting: i }));
          this.updateSorting(params, parent);
        }

        if (this.oldParent && this.oldParent.isRoot) {
          this.items = this.items.map((e) => {
            e.droppable = true;
            return e;
          });
        } else {
          const rootNode = this.items[0].parent;
          rootNode._vm.$el?.classList.remove("dragging-child");
        }
      });

      unsetScrollOnMouseMove();
    },
    buildTree(list) {
      const map = {};
      let node;
      let parent;
      const roots = [];
      let i;

      for (i = 0; i < list.length; i += 1) {
        list[i] = { ...list[i] };
        map[list[i].id] = i;
        list[i].parentId = list[i].parent;
        list[i].children = [];
        list[i].droppable = list[i].command == "tree";
      }

      for (i = 0; i < list.length; i += 1) {
        node = list[i];
        if (node.parent && list[map[node.parent]]) {
          parent = list[map[node.parent]];
          if (parent.command == "tree") {
            parent.children.push(node);
          }
        } else {
          roots.push(node);
        }
      }

      return roots;
    },
    searchTree(items, id) {
      for (let i = 0; i < items.length; i += 1) {
        if (items[i].id == id) {
          return items[i];
        }
        if (items[i].children && items[i].children.length > 0) {
          const data = this.searchTree(items[i].children, id);
          if (data) {
            return data;
          }
        }
      }
      return null;
    },
    updateTree() {
      const filterItems = this.originItems.map((e) => {
        const item = this.searchTree(this.items, e.id);
        e.open = item ? item.open : false;
        return e;
      });
      this.items.forEach((item) => {
        this.$refs.DraggableTree.deleteNode(item);
      });
      this.$nextTick(() => {
        this.items = this.buildTree(filterItems.map((e) => ({ ...e })));
      });
    },
    show() {
      this.loading = true;
      CallCenterRegistry.instruction
        .show()
        .then((response) => {
          this.originItems = response.data.data;
          this.updateTree();
        })
        .catch()
        .then(() => {
          this.loading = false;
        });
    },
    validateForm() {
      this.$refs.editForm.validate().then((success) => {
        if (success) {
          if (!this.data.id) {
            this.create();
          } else {
            this.submit();
          }
        }
      });
    },
    handleCreate(parent) {
      this.parent = parent;
      this.data.parent = parent?.id;
      this.data.voice = null;
      this.data.backVoice = null;
      this.data.localeCode = null;
      this.data.waitingSound = null;
      this.data.ringTune = null;
      this.data.id = null;
      this.data.memo = null;
      this.data.onKey = null;
      this.data.command = "tree";
      if (parent) {
        const selected = this.parent.children.map((e) => e.onKey);
        this.fields = this.fieldsCommand;
        this.filterSelected(selected);
      }
      this.showModal();
    },
    handleCreateGroup() {
      this.handleCreate(null);
      const selected = this.items.map((e) => e.onKey);
      this.fields = this.fieldsGroupComputed;
      this.filterSelected(selected);
    },
    handleUpdate(data) {
      this.data = data;
      this.showModal();

      this.fields =
        data.parent && !data.parent.isRoot
          ? this.fieldsCommand.map((e) => ({ ...e }))
          : this.fieldsGroupComputed.map((e) => ({ ...e }));

      const selected = data.parent?.children.map((e) => e.onKey) || [];
      this.filterSelected(selected, false);
    },
    handleDelete(data) {
      this.data = data;
      this.confirmDelete(data.id);
    },
    filterSelected(selected, autoFill = true) {
      this.fields = this.fields.map((e) => {
        const o = { ...e };
        if (o.key === "onKey") {
          o.options = o.options.filter((n) => !selected.includes(n.id));
        }
        return o;
      });

      if (autoFill) {
        if (selected.length == 0) {
          this.data.onKey = 1;
        } else {
          const max = Math.max(...selected);
          if (max < 9) {
            this.data.onKey = max + 1;
          }
        }
      }
    },
    async upload(file) {
      this.loading = true;
      try {
        const formData = new FormData();
        formData.append("image", file);
        const response = await FileRepository.upload(formData);
        return response.data.data;
      } catch (error) {
        console.log(error);
      }
      this.loading = false;
    },
    isAudioFile(obj) {
      try {
        return typeof obj.name === "string";
      } catch (e) {
        return false;
      }
    },
    async submit() {
      this.loading = true;
      this.data.loading = true;
      const payload = {};
      const {
        id,
        parentId,
        onKey,
        command,
        voice,
        backVoice,
        memo,
        localeCode,
        waitingSound,
        ringTune,
      } = this.data;

      if (this.isAudioFile(voice)) payload.voice = await this.upload(voice);
      if (this.isAudioFile(backVoice)) {
        payload.backVoice = await this.upload(backVoice);
      }
      if (this.isAudioFile(waitingSound)) {
        payload.waitingSound = await this.upload(waitingSound);
      } else if (!waitingSound?.url) payload.waitingSound = null;
      if (this.isAudioFile(ringTune)) {
        payload.ringTune = await this.upload(ringTune);
      } else if (!ringTune?.url) payload.ringTune = null;

      payload.parent = parentId;
      payload.memo = memo;
      payload.command = command;
      payload.onKey = onKey;
      payload.localeCode = localeCode;

      CallCenterRegistry.instruction
        .update(payload, id)
        .then((response) => {
          this.originItems = this.originItems.map((e) => {
            if (e.id === id) {
              return response.data.data;
            }
            return e;
          });
          this.updateTree();
        })
        .catch()
        .then(() => {
          this.hideModal();
          this.loading = false;
          this.data.loading = false;
        });
    },
    async create() {
      this.loading = true;
      let {
        parent,
        localeCode,
        onKey,
        voice,
        backVoice,
        memo,
        command,
        waitingSound,
        ringTune,
      } = this.data;

      const thisParent = this.searchTree(this.items, parent);
      const sorting = thisParent
        ? thisParent.children.length
        : this.items.length;

      if (voice) voice = await this.upload(voice);
      if (backVoice) backVoice = await this.upload(backVoice);
      if (waitingSound) waitingSound = await this.upload(waitingSound);
      if (ringTune) ringTune = await this.upload(ringTune);

      CallCenterRegistry.instruction
        .create({
          voice,
          backVoice,
          waitingSound,
          ringTune,
          parent,
          localeCode,
          onKey,
          sorting,
          memo,
          command,
        })
        .then((response) => {
          const { data } = response.data;
          this.originItems.push({ ...data, droppable: command == "tree" });
          this.updateTree();
        })
        .catch()
        .then(() => {
          this.hideModal();
          this.loading = false;
        });
    },
    delete(node) {
      this.loading = true;
      CallCenterRegistry.instruction
        .delete(node.id)
        .then((response) => {
          const originIndex = this.originItems.findIndex(
            (e) => e.id === node.id
          );
          if (originIndex > -1) {
            this.originItems.splice(originIndex, 1);
            this.$refs.DraggableTree.deleteNode(node);
          }
        })
        .catch()
        .then(() => {
          this.hideModal();
          this.loading = false;
        });
    },
    updateSorting(params, parent) {
      this.loading = true;
      parent._vm.$el?.classList.add("loading-children");
      CallCenterRegistry.instruction
        .sort(params)
        .then((response) => {
          this.originItems = this.originItems.map((e) => {
            const param = params.find((i) => i.id === e.id);
            if (param) {
              e.sorting = param.sorting;
            }
            return e;
          });
        })
        .catch()
        .then(() => {
          this.loading = false;
          parent._vm.$el?.classList.remove("loading-children");
        });
    },
    confirmDelete(id) {
      const node = this.searchTree(this.items, id);
      this.$bvModal
        .msgBoxConfirm(
          this.$t("alert.unableToRevertAfterDelete", {
            item: node.title,
          }),
          {
            title: `${this.$t("button.confirm")}!`,
            size: "sm",
            buttonSize: "sm",
            okVariant: "primary",
            okTitle: this.$t("button.delete"),
            cancelTitle: this.$t("button.cancel"),
            cancelVariant: "outline-secondary",
            hideHeaderClose: false,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.delete(node);
          }
        });
    },
    back() {
      this.$router.back();
    },
  },
  setup() {
    const fieldsCommand = FormInput;
    const fieldsGroup = FormInputGroup;
    const permissionType = "call-center"; // "modify-quick-message";

    return {
      fields: fieldsCommand.map((e) => ({ ...e })),
      fieldsCommand,
      fieldsGroup,
      permissionType,
      languageEn,
      languageKm,
      languageZh,
      languageVi,
    };
  },
};
</script>

<style lang="scss">
.he-tree {
  padding: 0;

  & > .tree-node {
    &.dragging-child {
      & > .tree-node-children {
        & > .draggable-placeholder {
          display: none !important;
        }
      }
    }

    & > div {
      & > .tree-node {
        border: none;
        margin-bottom: 16px;
        border-radius: 8px;
        box-shadow: 3px 1px 8px 3px #e8e8e8;

        .tree-node-inner {
          padding: 8px;
        }

        .draggable-placeholder {
          display: block;
        }

        .tree-node .tree-node-inner-back {
          padding-bottom: 8px;
          padding-right: 8px;
        }
      }

      .tree-node-children {
        padding: 0 0 0 30px;

        @media (max-width: 768px) {
          padding: 0px;
        }

        h4 {
          font-size: 0.95rem;
        }
      }
    }
  }

  .tree-node-inner-back {
    margin-bottom: 0 !important;
    @media (max-width: 768px) {
      padding-left: 0px !important;
    }

    h4 {
      font-size: 1.1rem;
    }

    @media (max-width: 420px) {
      h4 {
        font-size: 1rem;
      }
    }

    h6 {
      font-size: 0.9rem;
    }

    .tree-node-inner {
      padding: 0px !important;
    }
  }
}

.tree-node-inner {
  padding: 0;
  cursor: move;
}

.draggable-placeholder-inner {
  border: 1px dashed #0088f8;
  box-sizing: border-box;
  background: rgba(0, 136, 249, 0.09);
  color: #0088f9;
  text-align: center;
  padding: 0;
  display: flex;
  align-items: center;
}
</style>
